import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Button, Text } from '@/components/ui';
import {
  deleteTransaction,
  fetchAccounts,
  fetchAllTransactions,
  fetchCategories,
  fetchCurrencies,
  fetchFxRates,
  fetchTransactions,
  updateTransaction,
  type TransactionInput,
} from '@/lib/finance/api';
import {
  accountPickerLabel,
  categoryOptions,
  formatMoney,
  parseMoneyInput,
  type MoneyFormat,
} from '@/lib/finance/format';
import { monthStart } from '@/lib/finance/month';
import { incomeExpenseTotals, scaleFlowTotals, transactionFlow } from '@/lib/finance/report';
import type {
  Account,
  Category,
  Currency,
  FinanceCategoryKind,
  FxRate,
  Transaction,
} from '@/lib/finance/types';

const PAGE_LIMIT = 200;
const NUMBER_FORMAT_KEY = 'cs-finance-number-format';
const TOTALS_CURRENCY_KEY = 'cs-finance-totals-currency';

const ledgerCell = 'border-b border-border px-2 py-1 align-top';
const ledgerField =
  'box-border h-8 max-h-8 rounded border border-transparent bg-transparent px-1.5 py-0 text-sm leading-[30px] hover:border-border focus:border-accent focus:outline-none';

function TotalsFigures({
  expense,
  income,
  net,
  transfers,
  code,
  numberFormat,
}: {
  expense: number;
  income: number;
  net: number;
  transfers: number;
  code: string;
  numberFormat: MoneyFormat;
}) {
  const rows = [
    ['Debit', expense],
    ['Credit', income],
    ['Net', net],
    ['Transfers', transfers],
  ] as const;
  return (
    <div className="grid grid-cols-[5.25rem_9rem] items-baseline gap-x-3 gap-y-0.5 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <div className={label === 'Transfers' ? 'text-fg-muted' : ''}>{label}</div>
          <div
            className={`text-right tabular-nums ${
              label === 'Transfers' ? 'text-fg-muted' : 'font-semibold'
            }`}
          >
            {formatMoney(value, numberFormat)} {code}
          </div>
        </div>
      ))}
    </div>
  );
}

function MultiFilter({
  allLabel,
  noun,
  options,
  selected,
  onChange,
}: {
  allLabel: string;
  noun: string;
  options: Array<{ id: number; label: string }>;
  selected: number[];
  onChange: (ids: number[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find((option) => option.id === selected[0])?.label ?? allLabel)
        : `${selected.length} ${noun}`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="cs-input flex h-8 min-w-44 max-w-80 items-center justify-center truncate px-3 py-0 text-center"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {summary}
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 max-h-72 w-72 overflow-y-auto rounded-lg border border-border bg-surface-overlay p-2 shadow-xl">
          {options.length === 0 ? (
            <div className="px-1 py-1 text-sm text-fg-muted">None yet</div>
          ) : (
            options.map((option) => {
              const checked = selected.includes(option.id);
              return (
                <label
                  key={option.id}
                  className="flex items-center gap-2 px-1 py-1 text-sm text-fg"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      onChange(
                        checked
                          ? selected.filter((id) => id !== option.id)
                          : [...selected, option.id],
                      )
                    }
                  />
                  <span className="truncate">{option.label}</span>
                </label>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

function loadTotalsCurrency(): string {
  if (typeof localStorage === 'undefined') return 'CAD';
  try {
    const stored = localStorage.getItem(TOTALS_CURRENCY_KEY);
    return stored && /^[A-Z]{3}$/.test(stored) ? stored : 'CAD';
  } catch {
    return 'CAD';
  }
}

function loadNumberFormat(): MoneyFormat {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(NUMBER_FORMAT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as MoneyFormat;
    return {
      grouping: parsed.grouping !== false,
      decimals: parsed.decimals !== false,
    };
  } catch {
    return {};
  }
}

export function TransactionsLedger({
  month,
  onOpenSmartImport,
}: {
  month: string;
  onOpenSmartImport?: () => void;
}) {
  const router = useRouter();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filterAccounts, setFilterAccounts] = useState<number[]>([]);
  const [filterCategories, setFilterCategories] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [numberFormat, setNumberFormat] = useState<MoneyFormat>(loadNumberFormat);
  const [totalsCurrency, setTotalsCurrency] = useState(loadTotalsCurrency);
  const [monthTransactions, setMonthTransactions] = useState<Transaction[]>([]);
  const [fxRates, setFxRates] = useState<FxRate[]>([]);
  const [formatOpen, setFormatOpen] = useState(false);
  const formatMenuRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => categoryOptions(categories), [categories]);
  const categoryKind = useMemo(() => {
    const map = new Map<number, FinanceCategoryKind>();
    for (const category of categories) map.set(category.id, category.kind);
    return map;
  }, [categories]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    try {
      localStorage.setItem(NUMBER_FORMAT_KEY, JSON.stringify(numberFormat));
    } catch {
      // The table still follows the in-memory choice.
    }
  }, [numberFormat]);

  useEffect(() => {
    try {
      localStorage.setItem(TOTALS_CURRENCY_KEY, totalsCurrency);
    } catch {
      // The footer still follows the in-memory choice.
    }
  }, [totalsCurrency]);

  useEffect(() => {
    if (!formatOpen) return;
    function onPointer(event: MouseEvent) {
      if (!formatMenuRef.current?.contains(event.target as Node)) setFormatOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [formatOpen]);

  const listFilters = useMemo(
    () => ({
      month,
      accountId: filterAccounts.length > 0 ? filterAccounts : undefined,
      categoryId: filterCategories.length > 0 ? filterCategories : undefined,
      q: debouncedSearch || undefined,
    }),
    [month, filterAccounts, filterCategories, debouncedSearch],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [txPage, accountRows, categoryRows, currencyRows, allRows, rates] = await Promise.all([
        fetchTransactions({ ...listFilters, limit: PAGE_LIMIT }),
        fetchAccounts(),
        fetchCategories(),
        fetchCurrencies(),
        fetchAllTransactions(listFilters),
        fetchFxRates(monthStart(month), monthStart(month)),
      ]);
      setTransactions(txPage.transactions);
      setNextCursor(txPage.nextCursor);
      setAccounts(accountRows);
      setCategories(categoryRows);
      setCurrencies(currencyRows);
      setMonthTransactions(allRows);
      setFxRates(rates);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [listFilters]);

  // Stable ref so callbacks can recover by reloading without depending on `reload`.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  const refreshTotals = useCallback(async () => {
    const [allRows, rates] = await Promise.all([
      fetchAllTransactions(listFilters),
      fetchFxRates(monthStart(month), monthStart(month)),
    ]);
    setMonthTransactions(allRows);
    setFxRates(rates);
  }, [listFilters]);
  const refreshTotalsRef = useRef(refreshTotals);
  refreshTotalsRef.current = refreshTotals;

  useEffect(() => {
    void reload();
  }, [reload]);

  async function loadMore() {
    if (nextCursor == null) return;
    setLoadingMore(true);
    try {
      const page = await fetchTransactions({
        ...listFilters,
        cursor: nextCursor,
        limit: PAGE_LIMIT,
      });
      setTransactions((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        const next = [...prev];
        for (const row of page.transactions) {
          if (seen.has(row.id)) continue;
          seen.add(row.id);
          next.push(row);
        }
        return next;
      });
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }

  const patchRow = useCallback(async (id: number, patch: Partial<TransactionInput>) => {
    setError(null);
    try {
      const updated = await updateTransaction(id, patch);
      setTransactions((prev) => prev.map((t) => (t.id === id ? updated : t)));
      void refreshTotalsRef.current().catch(() => {
        // The row is saved. Totals catch up on the next reload.
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save edit');
      await reloadRef.current();
    }
  }, []);

  async function onDeleteRow(id: number) {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await deleteTransaction(id);
      setTransactions((prev) => prev.filter((t) => t.id !== id));
      void refreshTotalsRef.current().catch(() => {
        // The row is deleted. Totals catch up on the next reload.
      });
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  }

  async function onBulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} transaction${selected.size === 1 ? '' : 's'}?`)) {
      return;
    }
    try {
      await Promise.all([...selected].map((id) => deleteTransaction(id)));
      const removed = selected;
      setTransactions((prev) => prev.filter((t) => !removed.has(t.id)));
      void refreshTotalsRef.current().catch(() => {
        // The rows are deleted. Totals catch up on the next reload.
      });
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete selection');
      await reload();
    }
  }

  function toggleSelected(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === transactions.length ? new Set() : new Set(transactions.map((t) => t.id)),
    );
  }

  const totals = useMemo(() => {
    const flow = incomeExpenseTotals(categories, monthTransactions);
    let missingFx = 0;
    for (const tx of monthTransactions) {
      if (tx.amountUsd == null) missingFx += 1;
    }
    return {
      expense: flow.expenseUsd,
      income: flow.incomeUsd,
      transfers: flow.transferUsd,
      missingFx,
      net: flow.netUsd,
      count: monthTransactions.length,
    };
  }, [monthTransactions, categories]);

  const currencyCodes = useMemo(() => {
    const codes = new Set(currencies.map((currency) => currency.code));
    codes.add('CAD');
    codes.add('USD');
    return [...codes].sort();
  }, [currencies]);

  const chosenRate =
    totalsCurrency === 'USD'
      ? 1
      : (fxRates.find((rate) => rate.currencyCode === totalsCurrency)?.rateToUsd ?? null);
  const chosenTotals =
    chosenRate != null && chosenRate !== 0
      ? scaleFlowTotals(
          {
            incomeUsd: totals.income,
            expenseUsd: totals.expense,
            transferUsd: totals.transfers,
          },
          chosenRate,
        )
      : null;

  function onCategoryChange(tx: Transaction, value: string) {
    const categoryId = value ? Number(value) : null;
    const newKind = categoryId != null ? categoryKind.get(categoryId) : undefined;
    const patch: Partial<TransactionInput> = { categoryId };
    if (newKind !== 'transfer' && tx.toAccountId != null) {
      patch.toAccountId = null;
    }
    void patchRow(tx.id, patch);
  }

  return (
    <View>
      <View className="mb-3 flex-row flex-wrap items-center gap-2">
        <input
          className="cs-input h-8 w-56 py-0"
          placeholder="Search notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <MultiFilter
          allLabel="All accounts"
          noun="accounts"
          options={accounts.map((account) => ({
            id: account.id,
            label: accountPickerLabel(account),
          }))}
          selected={filterAccounts}
          onChange={setFilterAccounts}
        />
        <MultiFilter
          allLabel="All categories"
          noun="categories"
          options={options.map((option) => ({ id: option.id, label: option.label }))}
          selected={filterCategories}
          onChange={setFilterCategories}
        />
        <View className="flex-1" />
        <div ref={formatMenuRef} className="relative">
          <Button
            label="Format"
            size="sm"
            variant="secondary"
            onPress={() => setFormatOpen((open) => !open)}
          />
          {formatOpen ? (
            <div className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-border bg-surface-overlay p-2 shadow-xl">
              <label className="flex items-center gap-2 px-1 py-1 text-sm text-fg">
                <input
                  type="checkbox"
                  checked={numberFormat.decimals !== false}
                  onChange={(e) =>
                    setNumberFormat((current) => ({ ...current, decimals: e.target.checked }))
                  }
                />
                Decimals
              </label>
              <label className="flex items-center gap-2 px-1 py-1 text-sm text-fg">
                <input
                  type="checkbox"
                  checked={numberFormat.grouping !== false}
                  onChange={(e) =>
                    setNumberFormat((current) => ({ ...current, grouping: e.target.checked }))
                  }
                />
                Thousands
              </label>
            </div>
          ) : null}
        </div>
        <Button
          label="+ Add"
          size="sm"
          variant="primary"
          onPress={() => router.push('/finance/transactions/new')}
        />
        <Button
          label="Import"
          size="sm"
          variant="secondary"
          onPress={() => onOpenSmartImport?.()}
        />
        <Button
          label="Import history"
          size="sm"
          variant="secondary"
          onPress={() => router.push('/finance/transactions/smart-import')}
        />
      </View>

      {selected.size > 0 ? (
        <View className="mb-2 flex-row items-center gap-3">
          <Text variant="body-sm" className="text-muted">
            {selected.size} selected
          </Text>
          <Button
            label="Delete selected"
            size="sm"
            variant="danger"
            onPress={() => void onBulkDelete()}
          />
        </View>
      ) : null}

      {error ? (
        <Text variant="body-sm" className="mb-3 text-danger">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <ActivityIndicator />
      ) : transactions.length === 0 ? (
        <Text variant="body" className="text-muted">
          No transactions for this month. Use “+ Add” to record one.
        </Text>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="align-middle text-fg-muted">
                <th className="border-b border-border px-2 py-2 align-middle">
                  <div className="flex h-8 items-center justify-center">
                    <input
                      type="checkbox"
                      className="size-4"
                      aria-label="Select all"
                      checked={selected.size === transactions.length && transactions.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </div>
                </th>
                <th className="border-b border-border px-2 py-2 text-left font-medium">Date</th>
                <th className="border-b border-border px-2 py-2 text-left font-medium">Account</th>
                <th className="border-b border-border px-2 py-2 text-left font-medium">Category</th>
                <th className="border-b border-border px-2 py-2 text-right font-medium">Amount</th>
                <th className="border-b border-border px-2 py-2 text-right font-medium">USD</th>
                <th className="border-b border-border px-2 py-2 text-left font-medium">Notes</th>
                <th className="border-b border-border px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => {
                const flow = transactionFlow(categoryKind, tx);
                const isTransfer = flow === 'transfer';
                const directionClass =
                  flow === 'income' ? 'text-success' : flow === 'expense' ? 'text-danger' : '';
                const amountClass = [
                  ledgerField,
                  'w-32 text-right tabular-nums',
                  directionClass,
                ].join(' ');
                const usdText =
                  tx.amountUsd != null ? formatMoney(tx.amountUsd, numberFormat) : null;
                return (
                  <tr key={tx.id}>
                    <td className={ledgerCell}>
                      <div className="flex h-8 items-center justify-center">
                        <input
                          type="checkbox"
                          className="size-4"
                          aria-label={`Select transaction ${tx.id}`}
                          checked={selected.has(tx.id)}
                          onChange={() => toggleSelected(tx.id)}
                        />
                      </div>
                    </td>
                    <td className={ledgerCell}>
                      <input
                        key={`date:${tx.id}:${tx.date}`}
                        className={`${ledgerField} w-36 [&::-webkit-datetime-edit]:p-0`}
                        type="date"
                        defaultValue={tx.date}
                        onBlur={(e) => {
                          if (e.target.value && e.target.value !== tx.date) {
                            void patchRow(tx.id, { date: e.target.value });
                          }
                        }}
                      />
                    </td>
                    <td className={ledgerCell}>
                      <select
                        className={`${ledgerField} w-36`}
                        value={tx.accountId != null ? String(tx.accountId) : ''}
                        onChange={(e) =>
                          void patchRow(tx.id, {
                            accountId: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                      >
                        <option value="">None</option>
                        {accounts.map((account) => (
                          <option key={account.id} value={String(account.id)}>
                            {account.name}
                          </option>
                        ))}
                      </select>
                      {isTransfer ? (
                        <div className="mt-1 flex h-8 items-center gap-1">
                          <button
                            type="button"
                            className="text-fg-subtle underline"
                            title={
                              tx.amount < 0
                                ? 'Money arrives in this account'
                                : 'Money leaves this account'
                            }
                            onClick={() => void patchRow(tx.id, { amount: -tx.amount })}
                          >
                            {tx.amount < 0 ? '← From' : '→ To'}
                          </button>
                          <select
                            className={`${ledgerField} w-32`}
                            value={tx.toAccountId != null ? String(tx.toAccountId) : ''}
                            onChange={(e) =>
                              void patchRow(tx.id, {
                                toAccountId: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                          >
                            <option value="">{tx.amount < 0 ? 'From…' : 'To…'}</option>
                            {accounts.map((account) => (
                              <option key={account.id} value={String(account.id)}>
                                {account.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}
                    </td>
                    <td className={ledgerCell}>
                      <select
                        className={`${ledgerField} w-44`}
                        value={tx.categoryId != null ? String(tx.categoryId) : ''}
                        onChange={(e) => onCategoryChange(tx, e.target.value)}
                      >
                        <option value="">Uncategorized</option>
                        {options.map((option) => (
                          <option key={option.id} value={String(option.id)}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={ledgerCell}>
                      <div className="flex items-center justify-end gap-1">
                        <select
                          className={ledgerField}
                          value={tx.currencyCode}
                          onChange={(e) => void patchRow(tx.id, { currencyCode: e.target.value })}
                        >
                          {currencies.map((currency) => (
                            <option key={currency.code} value={currency.code}>
                              {currency.code}
                            </option>
                          ))}
                        </select>
                        <input
                          key={`amount:${tx.id}:${tx.amount}:${numberFormat.grouping}:${numberFormat.decimals}`}
                          className={amountClass}
                          inputMode="decimal"
                          defaultValue={formatMoney(tx.amount, numberFormat)}
                          onFocus={(e) => {
                            e.target.value = String(tx.amount);
                            e.target.select();
                          }}
                          onBlur={(e) => {
                            const value = parseMoneyInput(e.target.value);
                            if (Number.isFinite(value) && value !== tx.amount) {
                              void patchRow(tx.id, { amount: value });
                              return;
                            }
                            e.target.value = formatMoney(tx.amount, numberFormat);
                          }}
                        />
                      </div>
                    </td>
                    <td className={ledgerCell}>
                      <div className="flex justify-end">
                        <div
                          className={`box-border flex h-8 w-32 items-center justify-end px-1.5 text-sm tabular-nums leading-none ${directionClass}`}
                          title={usdText == null ? 'No FX rate for this month' : undefined}
                        >
                          {usdText ?? <span className="text-warning">no rate</span>}
                        </div>
                      </div>
                    </td>
                    <td className={ledgerCell}>
                      <input
                        key={`notes:${tx.id}:${tx.notes ?? ''}`}
                        className={`${ledgerField} w-48`}
                        defaultValue={tx.notes ?? ''}
                        onBlur={(e) => {
                          const value = e.target.value.trim();
                          if (value !== (tx.notes ?? '')) {
                            void patchRow(tx.id, { notes: value || null });
                          }
                        }}
                      />
                    </td>
                    <td className={ledgerCell}>
                      <div className="flex h-8 items-center justify-center">
                        <button
                          type="button"
                          className="flex size-8 items-center justify-center text-lg leading-none text-danger hover:underline"
                          aria-label={`Delete transaction ${tx.id}`}
                          onClick={() => void onDeleteRow(tx.id)}
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor != null ? (
        <View className="mt-3">
          <Button
            label={loadingMore ? 'Loading…' : 'Load more'}
            size="sm"
            variant="secondary"
            disabled={loadingMore}
            onPress={() => void loadMore()}
          />
        </View>
      ) : null}

      {!loading && !error ? (
        <View className="mt-4 gap-3 border-t border-border pt-3">
          <Text variant="body-sm" className="font-semibold">
            {totals.count} transaction{totals.count === 1 ? '' : 's'}
          </Text>
          <div className="flex flex-wrap items-start gap-x-12 gap-y-4">
            <div>
              <div className="mb-1 flex h-8 items-center text-sm font-semibold">USD</div>
              <TotalsFigures
                expense={totals.expense}
                income={totals.income}
                net={totals.net}
                transfers={totals.transfers}
                code="USD"
                numberFormat={numberFormat}
              />
            </div>
            <div>
              <select
                aria-label="Totals currency"
                className="cs-input mb-1 h-8 w-24 py-0"
                value={totalsCurrency}
                onChange={(event) => setTotalsCurrency(event.target.value)}
              >
                {currencyCodes.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
              {chosenTotals ? (
                <TotalsFigures
                  expense={chosenTotals.expense}
                  income={chosenTotals.income}
                  net={chosenTotals.net}
                  transfers={chosenTotals.transfers}
                  code={totalsCurrency}
                  numberFormat={numberFormat}
                />
              ) : (
                <Text variant="body-sm" className="text-warning">
                  No FX rate for {totalsCurrency} this month.
                </Text>
              )}
            </div>
          </div>
          {totals.missingFx > 0 ? (
            <Text variant="body-sm" className="text-fg-muted">
              {totals.missingFx} row{totals.missingFx === 1 ? '' : 's'} missing FX, left out of the
              totals
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
